import {
  Button,
  ButtonWithIcon,
  InputChip,
  TableSearchInput,
  TextField,
} from '@oy/uidesktop';
import { SystemIcon } from '@oy/uiicon';
import PropTypes from 'prop-types';
import { useTranslate } from 'src/hooks';
import NoLabelRecordsLimiter from './NoLabelRecordsLimiter';
import styles from './RecordsTable.module.css';

const RecordsTableHeader = props => {
  const {
    leftButtons,
    rightButtons,
    statusFilter,
    onFilterClick,
    onExportReport,
    onSearchIconClick,
    onSearchInputKeyDown,
    onSearchInputChange,
    onSearchInputBlur,
    searchInputName,
    searchInputValue,
    searchInputPlaceholder,
    searchHelpText,
    disabled,
    clickExportModal,
    filterTags,
    handleClearFilterInModal,
    disableRecordLimiter,
    disablePagination,
    pageSize,
    handlerOnRecordLimiterSubmit,
    SearchTooltip,
    isNewSearchFieldStyle,
    enableBottomRowRecords,
  } = props;

  const { getTranslation } = useTranslate();

  const isTagsEmpty = () => {
    for (const key in filterTags) {
      if (filterTags[key]) {
        return true;
      }
    }
    return false;
  };

  let filterChipIdx = 0;

  return (
    <>
      <div className="col-12 mb-1 d-flex justify-content-between">
        <div className="d-flex justify-content-start">
          {isNewSearchFieldStyle ? (
            <div className={SearchTooltip && styles.hastooltip}>
              <TableSearchInput
                name={searchInputName}
                placeholder={searchInputPlaceholder}
                value={searchInputValue}
                onChange={onSearchInputChange}
                onBlur={onSearchInputBlur}
                helpText={searchHelpText}
                onKeyDown={onSearchInputKeyDown}
                onSearchIconClick={onSearchIconClick}
              />
              {SearchTooltip && (
                <span className={styles.tooltipwrapper}>
                  <span className={styles.tooltip}>{SearchTooltip}</span>
                </span>
              )}
            </div>
          ) : (
            <div
              style={{ width: '259px' }}
              className={SearchTooltip && styles.hastooltip}
            >
              <TextField
                name={searchInputName}
                placeholder={searchInputPlaceholder}
                rightIcon={
                  <div onClick={onSearchIconClick}>
                    <SystemIcon name="search" />
                  </div>
                }
                value={searchInputValue}
                onChange={onSearchInputChange}
                onBlur={onSearchInputBlur}
                helpText={searchHelpText}
                onKeyDown={onSearchInputKeyDown}
                style={{ height: '40px', paddingRight: '34px' }}
              />
              {SearchTooltip && (
                <span className={styles.tooltipwrapper}>
                  <span className={styles.tooltip}>{SearchTooltip}</span>
                </span>
              )}
            </div>
          )}
          {statusFilter && (
            <div className="ml-2" style={{ zIndex: 2 }}>
              {statusFilter}
            </div>
          )}
          {onFilterClick && (
            <div className="ml-2">
              <ButtonWithIcon
                color={ButtonWithIcon.Color.Secondary}
                buttonType={ButtonWithIcon.Type.Outlined}
                iconName="filter"
                onClick={onFilterClick}
                style={{ width: '93px' }}
              >
                {getTranslation('common_filter')}
              </ButtonWithIcon>
            </div>
          )}
        </div>
        <div className="d-flex justify-content-end">
          {!disablePagination &&
            !disableRecordLimiter &&
            !enableBottomRowRecords && (
              <div style={{ zIndex: 2 }}>
                <NoLabelRecordsLimiter
                  disabled={disabled}
                  value={{ value: pageSize, label: `${pageSize} Rows` }}
                  onChange={handlerOnRecordLimiterSubmit}
                />
              </div>
            )}
          {leftButtons && <div className="ml-2">{leftButtons}</div>}
          {onExportReport && (
            <div className="ml-2">
              <ButtonWithIcon
                disabled={disabled}
                iconName="download"
                onClick={clickExportModal}
                color={ButtonWithIcon.Color.Primary}
                size={ButtonWithIcon.Size.Medium}
                buttonType={ButtonWithIcon.Type.Outlined}
              >
                {getTranslation('recordsTable_button_export')}
              </ButtonWithIcon>
            </div>
          )}
          {rightButtons && <div className="ml-2">{rightButtons}</div>}
        </div>
      </div>
      <div className="col-12 mb-2 d-flex justify-content-start">
        {filterTags &&
          Object.entries(filterTags).map(([key, value]) => {
            if (value) {
              filterChipIdx += 1;
              return (
                <div className={filterChipIdx === 1 ? '' : 'ml-1'} key={key}>
                  <div className={styles.inputChipContainer}>
                    <InputChip
                      onClose={() => {
                        handleClearFilterInModal(key);
                      }}
                      title={value}
                    />
                  </div>
                </div>
              );
            }
          })}
        {isTagsEmpty() && (
          <div className="ml-1">
            <Button
              buttonType={ButtonWithIcon.Type.Text}
              size={ButtonWithIcon.Size.Small}
              onClick={() => {
                handleClearFilterInModal();
              }}
            >
              {getTranslation('common_clearFilter')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

RecordsTableHeader.propTypes = {
  /** To disable export button */
  disabled: PropTypes.bool,
  /** Gets called when the user click on export modal */
  clickExportModal: PropTypes.func,
  /** The additional action buttons on the rightmost side */
  rightButtons: PropTypes.element,
  /** The additional action buttons on the leftmost side */
  leftButtons: PropTypes.element,
  /** The additional status filter component beside text input */
  statusFilter: PropTypes.element,
  /** Gets called when the user clicks on the filter button */
  onFilterClick: PropTypes.func,
  /** Gets called when the user clicks on the export button */
  onExportReport: PropTypes.func,
  /** If you want to enable use new header */
  isUseNewTableHeader: PropTypes.bool,
  /** Gets called when the user type on search input. Not used if use old header. */
  onSearchInputChange: PropTypes.func,
  /** Gets called when the user click outside of search input. Not used if use old header. */
  onSearchInputBlur: PropTypes.func,
  /** Gets called when the user hit enter. */
  onSearchInputKeyDown: PropTypes.func,
  /** Gets called when the user click on search input icon. */
  onSearchIconClick: PropTypes.func,
  /** Gets called when the user click on clear filter */
  handleClearFilterInModal: PropTypes.func,
  /** Filter from modal that shows below table */
  filterTags: PropTypes.object,

  searchInputName: PropTypes.string,
  searchInputValue: PropTypes.string,
  searchInputPlaceholder: PropTypes.string,
  searchHelpText: PropTypes.string,

  /** If you want to add tooltip under the search field */
  SearchTooltip: PropTypes.string,
  /** If you want to use new search field style */
  isNewSearchFieldStyle: PropTypes.bool,
};

export default RecordsTableHeader;
